import { Injectable } from '@angular/core';
import { CompletionMessage } from '@app-libs/model';
import { PdfFontLoaderService } from './pdf-font-loader.service';

// PDF Configuration Constants
const PDF_CONFIG = {
  DEFAULT_FONT_SIZE: 11,
  LINE_HEIGHT: 1.6,
  PAGE_MARGINS: [40, 60, 40, 60] as [number, number, number, number],
  FILENAME_MAX_LENGTH: 30,
  TITLE_MAX_LENGTH: 50,
  COLORS: {
    PRIMARY: '#2c3e50',
    TEXT: '#333333',
    CODE: '#d63384',
    BLOCKQUOTE: '#6c757d',
    LINK: '#2196f3',
    BACKGROUND: '#f8f9fa',
    INLINE_CODE_BG: '#f1f3f4',
    HR: '#cccccc'
  },
  HEADING_SIZES: [20, 18, 16, 14, 12, 11] as [number, number, number, number, number, number]
} as const;

// Interfaces
interface PDFMakeModule {
  createPdf: (docDefinition: any) => {
    download: (filename: string) => void;
    open: () => void;
    getBlob: (callback: (blob: Blob) => void) => void;
  };
  vfs?: any;
}

interface PDFMakeOptions {
  headingFontSizes: number[];
  headingBold: boolean;
  headingColor: string;
  headingMargin: number[];
  paragraphFontSize: number;
  paragraphColor: string;
  paragraphLineHeight: number;
  paragraphMargin: number[];
  paragraphAlignment: string;
  codeblockFont: string;
  codeblockBackground: string;
  codeblockFontSize: number;
  codeblockColor: string;
  codeblockMargin: number[];
  blockquoteColor: string;
  blockquoteItalics: boolean;
  blockquoteMargin: number[];
  linkColor: string;
  linkUnderline: boolean;
  listMargin: number[];
  listFontSize: number;
  listColor: string;
  hr: {
    width: number;
    color: string;
    margin: number[];
  };
  boldColor: string;
  inlineCodeFont: string;
  inlineCodeFontSize: number;
  inlineCodeColor: string;
  inlineCodeBackground: string;
  // Font configuration for mdpdfmake
  defaultFont?: string;
  tableFont?: string;
  font?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PdfExportService {
  private pdfMakeInstance: PDFMakeModule | null = null;
  private fontsLoaded = false;
  private robotoFontsAvailable = false;

  constructor(private fontLoader: PdfFontLoaderService) { }

  /**
   * Export message content as text-based PDF with lazy loading
   * @param message - CompletionMessage containing content and metadata
   * @returns Promise<void> - Resolves when PDF is generated and downloaded
   */
  async exportMessageAsPdf(message: CompletionMessage): Promise<void> {
    if (!message?.content) {
      throw new Error('Message content is required');
    }

    if (!this.checkBrowserSupport()) {
      throw new Error('PDF export is not supported in this browser');
    }

    const startTime = performance.now();

    try {
      // Lazy load PDF libraries
      await this.loadPdfLibraries();

      // Configure mdpdfmake options
      const options = this.createPdfOptions();

      // Convert markdown to PDFMake document definition
      const { mdpdfmake } = await import('mdpdfmake');
      const docDefinition = await mdpdfmake(message.content, options);

      // Add our custom document-level styling
      this.applyDocumentStyling(docDefinition);

      // Add metadata
      docDefinition.info = this.createDocumentMetadata(message);

      // Generate and download PDF
      const filename = this.generateFilename(message);
      const pdfDocGenerator = this.pdfMakeInstance?.createPdf(docDefinition);
      if (!pdfDocGenerator) {
        throw new Error('PDF generator not available');
      }
      pdfDocGenerator.download(filename);

      // Log performance metrics
      const endTime = performance.now();
      this.logPerformanceMetrics(startTime, endTime, message.content.length);
    } catch (error) {
      this.handleError('PDF export failed', error);
      throw new Error(
        error instanceof Error ? error.message : 'Failed to generate PDF document'
      );
    }
  }

  /**
   * Lazy load PDF libraries on demand
   */
  private async loadPdfLibraries(): Promise<void> {
    if (this.pdfMakeInstance && this.fontsLoaded) {
      return;
    }

    try {
      // Dynamic import for lazy loading - only load core PDFMake
      const pdfMakeModule = await import('pdfmake/build/pdfmake');
      this.pdfMakeInstance = pdfMakeModule.default || pdfMakeModule;
      
      // Configure Roboto fonts
      await this.configureRobotoFonts();
      this.fontsLoaded = true;
    } catch (error) {
      throw new Error('Failed to load PDF libraries: ' + (error as Error).message);
    }
  }

  /**
   * Configure Roboto fonts for PDFMake
   */
  private async configureRobotoFonts(): Promise<void> {
    try {
      // Load Roboto fonts using the font loader service
      const { vfs, fonts } = await this.fontLoader.loadRobotoFonts();
      
      // Configure PDFMake with custom VFS and fonts
      if (this.pdfMakeInstance) {
        this.pdfMakeInstance.vfs = vfs;
        (this.pdfMakeInstance as any).fonts = fonts;
        this.robotoFontsAvailable = true;
        
        console.debug('Roboto fonts configured successfully');
      }
    } catch (error) {
      // Fall back to system fonts if Roboto loading fails
      console.warn('Failed to configure Roboto fonts, falling back to system fonts:', error);
      this.robotoFontsAvailable = false;
      this.configureSystemFonts();
    }
  }

  /**
   * Configure system fonts for PDFMake (fallback)
   */
  private configureSystemFonts(): void {
    try {
      // Don't configure custom fonts - let PDFMake use its built-in defaults
      // This avoids font loading issues and works reliably across all environments
      console.debug('Using PDFMake default fonts for maximum compatibility');
      
      // PDFMake will use built-in fonts which are always available
    } catch (error) {
      // Non-critical error - PDFMake will use built-in defaults
      console.warn('Failed to configure system fonts:', error);
    }
  }

  /**
   * Create PDF options configuration
   */
  private createPdfOptions(): PDFMakeOptions {
    const baseOptions = {
      headingFontSizes: [...PDF_CONFIG.HEADING_SIZES],
      headingBold: true,
      headingColor: PDF_CONFIG.COLORS.PRIMARY,
      headingMargin: [0, 10, 0, 5],
      paragraphFontSize: PDF_CONFIG.DEFAULT_FONT_SIZE,
      paragraphColor: PDF_CONFIG.COLORS.TEXT,
      paragraphLineHeight: PDF_CONFIG.LINE_HEIGHT,
      paragraphMargin: [0, 0, 0, 10],
      paragraphAlignment: 'left', // Better readability than justify
      codeblockFont: 'Courier',
      codeblockBackground: PDF_CONFIG.COLORS.BACKGROUND,
      codeblockFontSize: 10,
      codeblockColor: PDF_CONFIG.COLORS.CODE,
      codeblockMargin: [10, 5, 10, 5],
      blockquoteColor: PDF_CONFIG.COLORS.BLOCKQUOTE,
      blockquoteItalics: true,
      blockquoteMargin: [20, 10, 0, 10],
      linkColor: PDF_CONFIG.COLORS.LINK,
      linkUnderline: true,
      listMargin: [0, 5, 0, 10],
      listFontSize: PDF_CONFIG.DEFAULT_FONT_SIZE,
      listColor: PDF_CONFIG.COLORS.TEXT,
      hr: {
        width: 515,
        color: PDF_CONFIG.COLORS.HR,
        margin: [0, 10, 0, 10]
      },
      boldColor: PDF_CONFIG.COLORS.PRIMARY,
      inlineCodeFont: 'Courier',
      inlineCodeFontSize: 10,
      inlineCodeColor: PDF_CONFIG.COLORS.CODE,
      inlineCodeBackground: PDF_CONFIG.COLORS.INLINE_CODE_BG,
    };

    // Only use Roboto font if it's available
    if (this.robotoFontsAvailable) {
      return {
        ...baseOptions,
        defaultFont: 'Roboto',
        font: 'Roboto'
      };
    } else {
      // Use PDFMake default fonts for maximum compatibility
      return baseOptions;
    }
  }

  /**
   * Apply document-level styling
   */
  private applyDocumentStyling(docDefinition: any): void {
    const defaultStyle: any = {
      fontSize: PDF_CONFIG.DEFAULT_FONT_SIZE,
      lineHeight: PDF_CONFIG.LINE_HEIGHT,
      color: PDF_CONFIG.COLORS.TEXT,
    };

    // Only set font if Roboto is available
    if (this.robotoFontsAvailable) {
      defaultStyle.font = 'Roboto';
    }

    docDefinition.defaultStyle = defaultStyle;
    docDefinition.pageMargins = PDF_CONFIG.PAGE_MARGINS;
    
    // Ensure all content uses appropriate font
    this.enforceFont(docDefinition.content);
  }

  /**
   * Create document metadata
   */
  private createDocumentMetadata(message: CompletionMessage): any {
    return {
      title: this.getDocumentTitle(message),
      author: 'Cerebro AI',
      subject: 'Chat Message Export',
      creator: 'Cerebro AI Chat',
      creationDate: new Date()
    };
  }

  /**
   * Generate a title for the document from the message content
   * @param message - CompletionMessage with content
   * @returns string - Document title
   */
  private getDocumentTitle(message: CompletionMessage): string {
    // Extract first line or first N characters as title
    const firstLine = message.content.split('\n')[0];
    const title = firstLine.length > PDF_CONFIG.TITLE_MAX_LENGTH
      ? firstLine.substring(0, PDF_CONFIG.TITLE_MAX_LENGTH) + '...'
      : firstLine;

    // Remove markdown formatting from title
    return title
      .replace(/[#*_`[\]()]/g, '')
      .trim() || 'Chat Message';
  }

  /**
   * Generate filename for PDF export
   * @param message - CompletionMessage with metadata
   * @returns string - Formatted filename
   */
  private generateFilename(message: CompletionMessage): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const messagePreview = message.content
      .substring(0, PDF_CONFIG.FILENAME_MAX_LENGTH)
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    const sanitizedPreview = messagePreview || 'message';
    return `${sanitizedPreview}-${timestamp}.pdf`;
  }

  /**
   * Enhanced error handling with proper logging
   */
  private handleError(context: string, error: unknown): void {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const errorDetails = {
      context,
      error: errorMessage,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    // Log error details (in production, this would go to a logging service)
    console.error(context, errorDetails);

    // In production, you might want to send to analytics/monitoring service
    // this.analyticsService.trackError(errorDetails);
  }

  /**
   * Log performance metrics
   */
  private logPerformanceMetrics(startTime: number, endTime: number, contentLength: number): void {
    const duration = endTime - startTime;
    const metrics = {
      duration: Math.round(duration),
      contentLength,
      timestamp: new Date().toISOString(),
      performance: {
        'time-to-pdf': duration,
        'chars-per-ms': contentLength / duration
      }
    };

    // Log performance metrics (in production, this would go to analytics)
    console.debug('PDF Export Performance:', metrics);

    // In production, you might want to send to analytics service
    // this.analyticsService.trackPerformance('pdf-export', metrics);
  }

  /**
   * Enforce appropriate font for all content (recursively)
   */
  private enforceFont(content: any): void {
    if (Array.isArray(content)) {
      content.forEach(item => this.enforceFont(item));
    } else if (content && typeof content === 'object') {
      // Set font for all text elements only if Roboto is available
      if (this.robotoFontsAvailable && (content.text !== undefined || content.content !== undefined)) {
        content.font = 'Roboto';
      }
      
      // Recursively check all properties
      Object.keys(content).forEach(key => {
        if (key === 'content' || key === 'text' || key === 'stack' || key === 'columns' || key === 'table') {
          this.enforceFont(content[key]);
        }
      });
    }
  }

  /**
   * Enhanced browser support checking
   * @returns boolean - True if supported
   */
  private checkBrowserSupport(): boolean {
    if (typeof window === 'undefined' || !window.document) {
      return false;
    }

    // Check for required browser features
    const requiredFeatures = [
      'Blob',
      'URL',
      'Promise',
      'localStorage'
    ];

    return requiredFeatures.every(feature => feature in window);
  }
}